import { z } from 'zod';

// src/common/primitives.ts
var DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
var DATE_FORMAT = "YYYY-MM-DD";
var DATE_TIME_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
var DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
var dateTimeString = z.string().regex(DATE_TIME_RE, `Must be a valid datetime in format: ${DATE_TIME_FORMAT}`);
var dateString = z.string().regex(DATE_RE, `Must be a valid date in format: ${DATE_FORMAT}`);
var rfc3339String = z.string();
var idParam = z.coerce.number().int().positive();
var uuidString = z.uuid({ error: "Must be a valid UUID" });
var hrErrorSchema = z.object({
  /** HR'ning HTTP statusi; `null` — HR javob bermadi (ulanish, timeout) */
  status: z.number().int().nullable(),
  /** HR javobidagi `code`: 1000 — autentifikatsiya, 3000 — validatsiya, 4007 — xodim yo'q */
  code: z.union([z.number(), z.string()]).nullable(),
  /** HR'ning asl xabari (`message`, `detail` yoki maydon xatolari), PINFL yashirilgan */
  message: z.string().nullable(),
  /** Javob kelmaganda — tarmoq xatosi kodi (`UND_ERR_CONNECT_TIMEOUT`, `ENOTFOUND`, `TimeoutError` …) */
  network_error: z.string().nullable(),
  /** So'ralgan HR yo'li (PINFL yashirilgan) */
  path: z.string()
});
var apiErrorSchema = z.object({
  trace_id: z.string().nullable().optional(),
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.record(z.string(), z.string()).optional(),
    trace: z.string().optional(),
    details: z.record(z.string(), z.unknown()).optional(),
    hr: hrErrorSchema.optional()
  })
});
var ERROR_CODES = {
  VALIDATION_FAILED: "VALIDATION_FAILED",
  INVALID_CONTENT_TYPE: "INVALID_CONTENT_TYPE",
  INVALID_JSON_BODY: "INVALID_JSON_BODY",
  INVALID_QUERY_PARAMS: "INVALID_QUERY_PARAMS",
  INVALID_PATH_PARAMS: "INVALID_PATH_PARAMS",
  ROUTER_ERROR: "ROUTER_ERROR"
};
var HR_ERROR_CODES = {
  /** HR: bunday xodim yo'q (404 `4007`) — kirish rad etiladi (403) */
  EMPLOYEE_NOT_FOUND: "HR_EMPLOYEE_NOT_FOUND",
  /** HR'ga ulanib bo'lmadi: ulanish timeout'i, DNS, rad etildi, TLS */
  UNREACHABLE: "HR_UNREACHABLE",
  /** HR belgilangan vaqtda javob bermadi */
  TIMEOUT: "HR_TIMEOUT",
  /** HR Kitobxonlik login-parolini qabul qilmadi (401/403) */
  AUTH_FAILED: "HR_AUTH_FAILED",
  /** HR xato qaytardi (5xx, kutilmagan 4xx) */
  ERROR: "HR_ERROR",
  /** HR javobi tushunarsiz (JSON emas) */
  INVALID_RESPONSE: "HR_INVALID_RESPONSE",
  /** HR so'ralgan tashkilotni tanimadi (400 `org_id`) */
  ORG_NOT_FOUND: "HR_ORG_NOT_FOUND",
  /** HR javobida xodimning tashkiloti yo'q */
  NO_ORGANIZATION: "HR_NO_ORGANIZATION",
  /** HR'da tashkilotda xodim yo'q (`numOfEmployees` 0) */
  NO_EMPLOYEES: "HR_NO_EMPLOYEES"
};
var PAGINATION_DEFAULT_SIZE = 10;
var PAGINATION_MAX_SIZE = 500;
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().optional(),
  size: z.coerce.number().int().optional()
});
var pageResponseSchema = (item) => z.object({
  number: z.number().int(),
  size: z.number().int(),
  numberOfElements: z.number().int(),
  content: z.array(item)
});
function normalizePagination(query) {
  let size = query.size ?? 0;
  if (size <= 0) size = PAGINATION_DEFAULT_SIZE;
  if (size > PAGINATION_MAX_SIZE) size = PAGINATION_MAX_SIZE;
  return { page: query.page ?? 0, size };
}
function toLimitOffset(query) {
  const { page, size } = normalizePagination(query);
  return { limit: size, offset: (Math.max(1, page) - 1) * size };
}
function buildPageResponse(query, content) {
  const { page, size } = normalizePagination(query);
  return { number: page, size, numberOfElements: content.length, content };
}
var SPIRITUAL_SPHERES = [
  "ethical",
  "professional",
  "legal-disciplinary",
  "spiritual-educational",
  "physical"
];
var spiritualSphereSchema = z.enum(SPIRITUAL_SPHERES);
var EMPLOYEE_BOOK_STATUSES = [
  "exam_scheduled",
  "exam_in_process",
  "exam_retake",
  "exam_passed",
  "exam_failed"
];
var employeeBookStatusSchema = z.enum(EMPLOYEE_BOOK_STATUSES);
var EXAM_STATUSES = ["in_process", "passed", "failed", "overdue"];
var examStatusSchema = z.enum(EXAM_STATUSES);
var UPLOAD_FILE_TYPES = [
  "bookcover",
  "ebook",
  "audiobook",
  "question",
  "examvideo"
];
var uploadFileTypeSchema = z.enum(UPLOAD_FILE_TYPES);
var FILE_TYPES = {
  BOOK_COVER: "bookcover",
  EBOOK: "ebook",
  AUDIOBOOK: "audiobook",
  QUESTION: "question",
  EXAM_RECORDING: "recording"
};
var BUCKETS = {
  BOOK_COVER: "bookcovers",
  EBOOK: "ebooks",
  AUDIOBOOK: "audiobooks",
  QUESTION: "questions",
  EXAM_RECORDING: "recordings"
};
var CHEAT_LOG_TYPES = [
  "TAB_BLUR",
  "SWITCH_ROUTE",
  "PAGE_RELOAD",
  "FACE_NOT_DETECTED",
  "PHOTO_DETECTED",
  "MULTIPLE_FACES",
  "DIFFERENT_PERSON",
  "MOUTH_OPENING_DETECTED"
];

// src/common/alphabet.ts
var OKINA = "\u2018\u02BB'`\u2019";
var OLD_OKINA = "\u2018";
var OKINA_RE = new RegExp(`([OoGg])[${OKINA}]`, "g");
var DIGRAPH_RE = /([SsCc])[Hh]/g;
var NEW_LETTERS_RE = /[ÖöĞğŞşÇç]/g;
var isLetter = (ch) => !!ch && /\p{L}/u.test(ch);
var isUpper = (ch) => isLetter(ch) && ch === ch.toUpperCase() && ch !== ch.toLowerCase();
var isLower = (ch) => isLetter(ch) && ch === ch.toLowerCase() && ch !== ch.toUpperCase();
var NEW_OF = {
  O: "\xD6",
  o: "\xF6",
  G: "\u011E",
  g: "\u011F",
  S: "\u015E",
  s: "\u015F",
  C: "\xC7",
  c: "\xE7"
};
function toNewLatin(text) {
  return text.replace(OKINA_RE, (match, letter, offset) => {
    const prev = text[offset - 1];
    const next = text[offset + 2];
    if ((letter === "O" || letter === "o") && !isLetter(next)) return match;
    if (isUpper(letter) && isUpper(prev) && isLower(next)) return match;
    return NEW_OF[letter];
  }).replace(DIGRAPH_RE, (_match, letter) => NEW_OF[letter]);
}
var WORD_RE = new RegExp(`[\\p{L}${OKINA}]+`, "gu");
var FOREIGN_RE = /c(?!h)|w|th|ph/i;
var MIXED_CASE_RE = /\p{Ll}\p{Lu}/u;
function toNewLatinText(text) {
  return text.replace(
    WORD_RE,
    (word) => FOREIGN_RE.test(word) || MIXED_CASE_RE.test(word) ? word : toNewLatin(word)
  );
}
function toOldLatin(text) {
  return text.replace(NEW_LETTERS_RE, (ch, offset) => {
    switch (ch) {
      case "\xD6":
        return `O${OLD_OKINA}`;
      case "\xF6":
        return `o${OLD_OKINA}`;
      case "\u011E":
        return `G${OLD_OKINA}`;
      case "\u011F":
        return `g${OLD_OKINA}`;
      case "\u015F":
        return "sh";
      case "\xE7":
        return "ch";
      default: {
        const next = text[offset + 1];
        const allCaps = isLetter(next) ? isUpper(next) : isUpper(text[offset - 1]);
        return (ch === "\u015E" ? "S" : "C") + (allCaps ? "H" : "h");
      }
    }
  });
}
function latinSearchVariants(query) {
  const fresh = toNewLatin(query);
  const old = toOldLatin(fresh);
  const variants = [query, fresh, old];
  for (const mark of OKINA) variants.push(old.replaceAll(OLD_OKINA, mark));
  return [...new Set(variants)];
}
var MARKS_RE = /[‘ʻ`’]/g;
function latinFold(text) {
  return toNewLatin(text).toLowerCase().replace(MARKS_RE, "'");
}
var genreSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var genreListResponseSchema = z.object({ data: z.array(genreSchema) });
var createGenreSchema = z.object({ name: z.string().min(1).max(100) });
var updateGenreSchema = z.object({ name: z.string().min(1).max(255) });
var subjectSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var subjectListResponseSchema = z.object({ data: z.array(subjectSchema) });
var createSubjectSchema = z.object({ name: z.string().min(1).max(255) });
var updateSubjectSchema = z.object({ name: z.string().min(1).max(255) });
var fileUploadResponseSchema = z.object({
  id: z.string(),
  bucket: z.string(),
  type: z.string(),
  content_type: z.string(),
  name: z.string(),
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var uploadFileQuerySchema = z.object({ type: uploadFileTypeSchema });
var EBOOK_MAX_MB = 50;
var EBOOK_MAX_BYTES = EBOOK_MAX_MB * 1024 * 1024;
var FILE_CODES = {
  INVALID_BOOK_COVER_FILE: "INVALID_BOOK_COVER_FILE",
  INVALID_EBOOK_FILE: "INVALID_EBOOK_FILE",
  INVALID_AUDIOBOOK_FILE: "INVALID_AUDIOBOOK_FILE",
  INVALID_QUESTION_FILE: "INVALID_QUESTION_FILE",
  INVALID_EXAM_VIDEO_FILE: "INVALID_EXAM_VIDEO_FILE",
  /** Yangi: fayl turi chegarasidan katta (hozircha faqat ebook — `EBOOK_MAX_BYTES`) */
  FILE_TOO_LARGE: "FILE_TOO_LARGE",
  FILE_NOT_FOUND: "FILE_NOT_FOUND",
  FILE_ALREADY_EXIST: "FILE_ALREADY_EXIST",
  OBJECTS_NOT_FOUND: "OBJECTS_NOT_FOUND"
};
var int = z.number().int();
var optionalInt = z.coerce.number().int().optional();
var bookSchema = z.object({
  id: int,
  org_id: int,
  title: z.string(),
  author: z.string().nullable(),
  year: int.nullable(),
  pages: int.nullable(),
  genre_id: int.nullable(),
  subject_id: int.nullable(),
  cover_image_file_id: z.string().nullable(),
  audiobook_file_id: z.string().nullable(),
  ebook_file_id: z.string().nullable(),
  bonus_percent: z.number(),
  spiritual_sphere: z.string(),
  download_count: int,
  exam_passed_count: int,
  average_rating: z.number(),
  created_by: int,
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var adminBookListItemSchema = bookSchema.extend({
  question_count: int,
  org_name: z.string().nullable()
});
var adminBookListResponseSchema = pageResponseSchema(adminBookListItemSchema);
var adminBookListQuerySchema = paginationQuerySchema.extend({
  genre_id: optionalInt,
  subject_id: optionalInt,
  search: z.string().optional(),
  year: optionalInt,
  /** ESKI API'DA YO'Q (2026-10-14): ma'naviy soha bo'yicha filtr */
  spiritual_sphere: spiritualSphereSchema.optional()
});
var createBookSchema = z.object({
  title: z.string().min(1).max(255),
  author: z.string().max(255).nullable().optional(),
  genre_id: int.nullable().optional(),
  subject_id: int.nullable().optional(),
  year: int.nullable().optional(),
  pages: int.nullable().optional(),
  cover_image_file_id: uuidString.nullable().optional(),
  audiobook_file_id: uuidString.nullable().optional(),
  ebook_file_id: uuidString.nullable().optional(),
  bonus_percent: z.number().optional(),
  spiritual_sphere: spiritualSphereSchema
});
var updateBookSchema = createBookSchema.extend({
  author: z.string().min(1).max(255),
  cover_image_file_id: z.string().nullable().optional()
});
var employeeBookItemSchema = z.object({
  id: int,
  org_id: int,
  title: z.string(),
  author: z.string().nullable(),
  /**
   * ESKI API'DA YO'Q — ataylab qo'shilgan maydon (2026-09-24).
   *
   * Asosiy sahifa tavsiyani ma'naviy soha bo'yicha tanlaydi, xodim ro'yxati
   * esa sohani qaytarmasdi. Qo'shimcha maydon eski iste'molchilarni buzmaydi
   * (eski HR UI notanish maydonni e'tiborsiz qoldiradi). `z.string()` —
   * bazada enum'dan tashqari qiymat bo'lsa ro'yxat yiqilmasin (admin
   * `bookSchema` da ham shunday).
   */
  spiritual_sphere: z.string(),
  year: int.nullable(),
  pages: int.nullable(),
  genre_id: int.nullable(),
  subject_id: int.nullable(),
  cover_image_id: z.string().nullable(),
  audiobook_file_id: z.string().nullable(),
  ebook_file_id: z.string().nullable(),
  bonus_percent: z.number(),
  download_count: int,
  exam_passed_count: int,
  average_rating: z.number(),
  created_by: int,
  favorite: z.boolean(),
  enrolled: z.boolean(),
  employee_status: z.string().nullable(),
  passed_at: dateTimeString.nullable(),
  last_exam_id: int.nullable(),
  questions_enough: z.boolean()
});
var employeeBookListResponseSchema = pageResponseSchema(employeeBookItemSchema);
var employeeBookListQuerySchema = paginationQuerySchema.extend({
  status: employeeBookStatusSchema.optional(),
  employee_book_status: employeeBookStatusSchema.optional(),
  genre_id: optionalInt,
  subject_id: optionalInt,
  search: z.string().optional(),
  year: optionalInt
});
var rateBookSchema = z.object({
  rating: z.number().min(1).max(5),
  review: z.string().max(512).nullable().optional()
});
var toggleFavoriteResponseSchema = z.object({ favorite: z.boolean() });
var scheduleExamSchema = z.object({
  event_name: z.string().min(1),
  time: dateTimeString,
  description: z.string().max(512).nullable().optional(),
  color_code: z.string().max(256).nullable().optional()
});
var orgIdQuerySchema = z.object({ org_id: z.coerce.number().int() });
var booksCountResponseSchema = z.object({ org_id: int, count: int });
var scheduledListQuerySchema = paginationQuerySchema.extend({
  org_id: optionalInt,
  book_id: optionalInt
});
var scheduledListItemSchema = z.object({
  id: int,
  employee: z.object({ id: int, pinfl: z.string(), name: z.string(), passport_image: z.string() }),
  book: z.object({ id: int, title: z.string(), author: z.string().nullable() }),
  status: z.string(),
  scheduled_at: dateTimeString,
  exam_time: dateTimeString.nullable()
});
var scheduledListResponseSchema = pageResponseSchema(scheduledListItemSchema);
var BOOK_CODES = {
  BOOK_NOT_FOUND: "BOOK_NOT_FOUND",
  BOOK_ALREADY_EXIST: "BOOK_ALREADY_EXIST",
  EMPLOYEE_BOOK_NOT_FOUND: "EMPLOYEE_BOOK_NOT_FOUND",
  EMPLOYEE_BOOK_ALREADY_EXIST: "EMPLOYEE_BOOK_ALREADY_EXIST",
  BOOK_REVIEW_NOT_FOUND: "BOOK_REVIEW_NOT_FOUND",
  BOOK_REVIEW_ALREADY_EXISTS: "BOOK_REVIEW_ALREADY_EXISTS",
  FAVORITE_BOOK_NOT_FOUND: "FAVORITE_BOOK_NOT_FOUND",
  FAVORITE_BOOK_ALREADY_EXIST: "FAVORITE_BOOK_ALREADY_EXIST",
  EMPLOYEE_DOWNLOADED_BOOK_NOT_FOUND: "EMPLOYEE_DOWNLOADED_BOOK_NOT_FOUND",
  EMPLOYEE_DOWNLOADED_BOOK_ALREADY_EXIST: "EMPLOYEE_DOWNLOADED_BOOK_ALREADY_EXIST",
  QUESTION_NOT_ENOUGH_TO_SCHEDULE_EXAM: "QUESTION_NOT_ENOUGH_TO_SCHEDULE_EXAM"
};
var memberOrgPairQuerySchema = z.object({
  host_org_id: z.coerce.number().int(),
  member_org_id: z.coerce.number().int()
});
var listMemberOrgsQuerySchema = z.object({ host_org_id: z.coerce.number().int() });
var memberOrgListResponseSchema = z.object({
  data: z.array(z.object({ id: z.number().int(), name: z.string().nullable() }))
});
var LIBRARY_CODES = {
  MEMBER_ORGANIZATION_NOT_FOUND: "MEMBER_ORGANIZATION_NOT_FOUND",
  MEMBER_ORGANIZATION_ALREADY_EXIST: "MEMBER_ORGANIZATION_ALREADY_EXIST"
};
var orgId = z.number().int().positive();
var organizationItemSchema = z.object({
  id: orgId,
  name: z.string().nullable(),
  /** Shu tashkilotdan tizimga kirgan xodimlar */
  users_count: z.number().int(),
  /** Shu tashkilotga biriktirilgan (o'chirilmagan) kitoblar */
  books_count: z.number().int()
});
var organizationsResponseSchema = z.object({
  data: z.array(organizationItemSchema)
});
var organizationBookSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  author: z.string().nullable(),
  spiritual_sphere: z.string(),
  cover_image_id: z.string().nullable(),
  /** Qachon biriktirilgan */
  linked_at: dateTimeString
});
var organizationDetailSchema = organizationItemSchema.extend({
  books: z.array(organizationBookSchema)
});
var bookOrganizationsResponseSchema = z.object({
  data: z.array(z.object({ id: orgId, name: z.string().nullable() }))
});
var setBookOrganizationsSchema = z.object({
  org_ids: z.array(orgId).max(1e3)
});
var int2 = z.number().int();
var QUESTION_DIFFICULTIES = ["easy", "medium", "hard"];
var questionDifficultySchema = z.enum(QUESTION_DIFFICULTIES);
var count = int2.min(0).max(1e3);
var difficultyMixSchema = z.object({ easy: count, medium: count, hard: count });
var DEFAULT_DIFFICULTY_MIX = { easy: 3, medium: 5, hard: 2 };
var difficultyMixSumMessage = (n) => `savollar yi\u011Findisi ${n} ta b\xF6li\u015Fi kerak`;
var difficultyMixExceedsMessage = (available) => `kitobda bu darajadagi savollar ${available} ta`;
function splitByDifficultyMix(total, mix) {
  const weightSum = mix.easy + mix.medium + mix.hard;
  if (weightSum <= 0) return { easy: 0, medium: 0, hard: 0 };
  const shares = QUESTION_DIFFICULTIES.map((level, order) => {
    const weight = mix[level];
    const exact = total * weight / weightSum;
    return { level, order, weight, count: Math.floor(exact), rest: exact % 1 };
  });
  let left = total - shares.reduce((sum, s) => sum + s.count, 0);
  const byRest = [...shares].sort(
    (a, b) => b.rest - a.rest || b.weight - a.weight || a.order - b.order
  );
  for (const share of byRest) {
    if (left <= 0) break;
    share.count += 1;
    left -= 1;
  }
  return { easy: shares[0].count, medium: shares[1].count, hard: shares[2].count };
}
var globalDifficultyMixResponseSchema = z.object({
  easy: int2,
  medium: int2,
  hard: int2,
  updated_at: dateTimeString.nullable()
});
var countsSchema = z.object({ easy: int2, medium: int2, hard: int2 });
var bookDifficultyMixResponseSchema = z.object({
  book_id: int2,
  /** Shu kitobga berilgani; `null` — umumiy ishlatiladi */
  book_mix: countsSchema.nullable(),
  global_mix: countsSchema,
  /** Imtihonda amalda ishlatiladigani (yig'indisi `exam_questions_count`) */
  effective_mix: countsSchema,
  /** Kitobdagi (o'chirilmagan) savollar soni, qiyinlik bo'yicha; `unset` — qiyinligi yo'q */
  available: countsSchema.extend({ unset: int2 }),
  /** Bitta imtihondagi savollar soni */
  exam_questions_count: int2
});

// src/question/question.schema.ts
var int3 = z.number().int();
var answerOptionSchema = z.object({
  id: z.string(),
  option_text: z.string(),
  is_correct: z.boolean()
});
var answerOptionsSchema = z.object({ options: z.array(answerOptionSchema) });
var questionSchema = z.object({
  id: int3,
  book_id: int3,
  question_text: z.string(),
  answer_options: answerOptionsSchema,
  difficulty: questionDifficultySchema.nullable(),
  source_page_start: int3.nullable(),
  source_page_end: int3.nullable(),
  source_text: z.string().nullable(),
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var questionListResponseSchema = pageResponseSchema(questionSchema);
var listQuestionsQuerySchema = paginationQuerySchema.extend({
  book_id: z.coerce.number().int()
});
var createQuestionSchema = z.object({
  book_id: int3,
  question_text: z.string().min(1).max(512),
  /** Eskida yo'q: yuborilmasa yoki `null` — qiyinligi belgilanmagan */
  difficulty: questionDifficultySchema.nullable().optional(),
  answer_options: z.array(
    z.object({
      option_text: z.string().min(1).max(255),
      is_correct: z.boolean().optional()
    })
  ).min(2)
});
var updateQuestionSchema = z.object({
  question_text: z.string().min(1).max(512),
  /** Eskida yo'q: yuborilmasa — o'zgarmaydi, `null` — olib tashlanadi */
  difficulty: questionDifficultySchema.nullable().optional(),
  answer_options: z.array(
    z.object({
      id: uuidString.nullable().optional(),
      option_text: z.string().min(1).max(255),
      is_correct: z.boolean().optional()
    })
  ).min(2)
});
var importQuestionsSchema = z.object({
  file_id: z.string().min(1),
  book_id: int3
});
var importQuestionsResponseSchema = z.object({
  skipped_rows: z.array(z.object({ row_number: int3, error: z.string() }))
});
var QUESTION_CODES = {
  QUESTION_NOT_FOUND: "QUESTION_NOT_FOUND",
  QUESTION_ALREADY_EXISTS: "QUESTION_ALREADY_EXISTS",
  /** Qiyinlik bo'yicha sonlar yig'indisi imtihondagi savollar soniga teng emas */
  DIFFICULTY_MIX_SUM_INVALID: "DIFFICULTY_MIX_SUM_INVALID",
  /** Kitobga berilgan son shu darajadagi savollardan ko'p */
  DIFFICULTY_MIX_EXCEEDS_AVAILABLE: "DIFFICULTY_MIX_EXCEEDS_AVAILABLE"
};
var int4 = z.number().int();
var pageRangeSchema = z.object({ start: int4, end: int4 });
function reviewPageRanges(questions) {
  const ranges = questions.flatMap((q) => {
    const start = q.source_page_start ?? q.source_page_end;
    const end = q.source_page_end ?? q.source_page_start;
    if (start === null || end === null) return [];
    return [{ start: Math.min(start, end), end: Math.max(start, end) }];
  });
  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged = [];
  for (const range of ranges) {
    const last = merged.at(-1);
    if (last && range.start <= last.end + 1) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}
var EXAM_VIOLATION_LIMITS = {
  /** Imtihon oynasidan chiqish (blur, sahifa almashtirish, yangilash): shundan KO'P bo'lsa yakunlanadi */
  tab_leaves: 3,
  /** Yuz holatlari (yuz yo'q, rasm, ko'p odam, boshqa odam) jami: shuncha bo'lsa yakunlanadi */
  face_events: 5,
  /** Bitta yuz holati shuncha soniya uzluksiz davom etsa yakunlanadi */
  face_seconds: 10,
  /** Yuz holati shuncha soniya davom etsa qayd etiladi (qisqa miltillash sanalmaydi) */
  face_min_seconds: 2,
  /** Gapirish (og'iz ochish): shuncha bo'lsa yakunlanadi */
  mouth_openings: 10,
  /** Brauzer yon paneli / DevTools ochilishi (2026-10-16): shuncha bo'lsa yakunlanadi (birinchisi — ogohlantirish) */
  panel_openings: 2,
  /** Ochiq panel shuncha soniyada yopilmasa yakunlanadi */
  panel_seconds: 10,
  /** Skrinshot tugmalari (2026-10-16): shuncha bo'lsa yakunlanadi (birinchisi — ogohlantirish) */
  screenshots: 2
};
var int5 = z.number().int();
var examViolationLimitsSchema = z.object({
  tab_leaves: int5,
  face_events: int5,
  face_seconds: int5,
  face_min_seconds: int5,
  mouth_openings: int5,
  panel_openings: int5,
  panel_seconds: int5,
  screenshots: int5
});
var EXAM_FINISH_REASONS = [
  "manual",
  "time",
  "reload",
  "tab_leaves",
  "face_events",
  "mouth",
  "no_face",
  "photo",
  "multiple_faces",
  "different_person",
  "side_panel",
  "side_panel_open",
  "screenshot",
  "late",
  "violations",
  "overdue",
  "admin"
];
var examFinishReasonSchema = z.enum(EXAM_FINISH_REASONS);
var CLIENT_FINISH_REASONS = [
  "manual",
  "time",
  "reload",
  "tab_leaves",
  "face_events",
  "mouth",
  "no_face",
  "photo",
  "multiple_faces",
  "different_person",
  "side_panel",
  "side_panel_open",
  "screenshot"
];
function isExamFinishReason(v) {
  return EXAM_FINISH_REASONS.includes(v);
}

// src/exam/exam.schema.ts
var int6 = z.number().int();
var optionalInt2 = z.coerce.number().int().optional();
var score = z.number();
var examEmployeeSchema = z.object({ id: int6, pinfl: z.string(), name: z.string() });
var examBookSchema = z.object({
  id: int6,
  title: z.string(),
  author: z.string().nullable()
});
var examQuestionSchema = z.object({
  id: int6,
  order_number: int6,
  question_text: z.string(),
  answer_options: z.object({
    options: z.array(z.object({ id: z.string(), option_text: z.string() }))
  }),
  selected_answer_option_id: z.string().nullable(),
  is_correct: z.boolean().nullable()
});
var examResponseSchema = z.object({
  id: int6,
  org_id: int6,
  employee: examEmployeeSchema,
  book: examBookSchema,
  book_id: int6,
  employee_pinfl: z.string(),
  status: z.string(),
  passing_score_percent: int6,
  started_at: dateTimeString,
  ended_at: dateTimeString.nullable(),
  passed_at: dateTimeString.nullable(),
  deadline: dateTimeString,
  recording_file_id: z.string().nullable(),
  total_questions_count: int6,
  total_answers_count: int6,
  created_at: dateTimeString,
  updated_at: dateTimeString,
  questions: z.array(examQuestionSchema)
});
var startExamQuerySchema = z.object({ book_id: z.coerce.number().int() });
var finishExamSchema = z.object({
  answers: z.array(z.object({ question_id: int6, answer_option_id: uuidString })).optional().default([]),
  /** ESKI API'DA YO'Q (2026-10-14): nega yakunlandi (`EXAM_FINISH_REASONS`, xona yuboradiganlari) */
  finish_reason: z.enum(CLIENT_FINISH_REASONS).optional()
});
var examResultResponseSchema = z.object({
  id: int6,
  book_id: int6,
  started_at: dateTimeString,
  percent: int6,
  score,
  status: z.string(),
  user_rating: z.number().nullable(),
  total_questions_count: int6,
  total_answers_count: int6,
  questions: z.array(z.object({ id: int6, order_number: int6, correct: z.boolean().nullable() })),
  /**
   * ESKI API'DA YO'Q (2026-10-15): to'g'ri javob berilmagan (xato yoki javobsiz)
   * savollar asoslangan kitob sahifalari — qayta o'qish tavsiyasi uchun,
   * birlashtirilgan oraliqlar (`reviewPageRanges`). Sahifasi yo'q savollar
   * (qo'lda/eski) kirmaydi; hammasi to'g'ri yoki sahifa yo'q bo'lsa — `[]`.
   */
  review_pages: z.array(pageRangeSchema)
});
var employeeExamListItemSchema = z.object({
  id: int6,
  org_id: int6,
  passing_score_percent: int6,
  started_at: dateTimeString,
  ended_at: dateTimeString.nullable(),
  deadline: dateTimeString,
  status: z.string(),
  total_questions_count: int6,
  total_answers_count: int6,
  score,
  violation_count: int6,
  created_at: dateTimeString,
  updated_at: dateTimeString,
  book: examBookSchema
});
var employeeExamListResponseSchema = pageResponseSchema(employeeExamListItemSchema);
var employeeExamListQuerySchema = paginationQuerySchema.extend({
  status: examStatusSchema.optional(),
  started_at_from: dateTimeString.optional(),
  started_at_to: dateTimeString.optional()
});
var adminExamListItemSchema = z.object({
  id: int6,
  org_id: int6,
  /** ESKI API'DA YO'Q (2026-10-13): tashkilot nomi (HR); `null` — nom yo'q */
  org_name: z.string().nullable(),
  employee: examEmployeeSchema,
  book: examBookSchema,
  passing_score_percent: int6,
  started_at: dateTimeString,
  ended_at: dateTimeString.nullable(),
  deadline: dateTimeString,
  recording_file_id: z.string().nullable(),
  status: z.string(),
  total_questions_count: int6,
  total_answers_count: int6,
  score,
  violation_count: int6,
  created_at: dateTimeString,
  updated_at: dateTimeString,
  /**
   * ESKI API'DA YO'Q (2026-10-14): nega yakunlandi (`EXAM_FINISH_REASONS`);
   * `null` — jarayonda yoki shu o'zgarishdan oldingi imtihon. Satr —
   * keyinchalik olib tashlangan qiymat javobni yiqitmasin.
   */
  finish_reason: z.string().nullable(),
  /** ESKI API'DA YO'Q (2026-10-14): imtihon boshlangandagi chegaralar; `null` — eski imtihon */
  violation_limits: examViolationLimitsSchema.partial().nullable()
});
var adminExamListResponseSchema = pageResponseSchema(adminExamListItemSchema);
var adminExamListQuerySchema = employeeExamListQuerySchema.extend({
  org_id: optionalInt2,
  employee_id: optionalInt2,
  employee_pinfl: z.string().optional(),
  book_id: optionalInt2
});
var changeExamStatusSchema = z.object({ id: int6, status: z.string().min(1) });
var changeExamStatusResponseSchema = z.object({ id: int6, status: z.string() });
var deleteExamResponseSchema = z.object({
  exam_id: int6,
  /**
   * Xodimning shu kitobdagi holati — qolgan imtihonlardan qayta hisoblangani
   * (`EMPLOYEE_BOOK_STATUSES`); `null` — kitob yozuvi yo'q.
   */
  employee_book_status: z.string().nullable(),
  deleted: z.object({
    /** `exam_questions` qatorlari (savollar va javoblar) */
    questions: int6,
    /** Qoidabuzarlik jurnali yozuvlari */
    logs: int6,
    /** Video (`files` qatori; MinIO obyekti QOLADI) — 0 yoki 1 */
    recordings: int6
  })
});
var scheduleEventSchema = z.object({
  id: int6,
  event: z.string(),
  time: dateTimeString,
  description: z.string().nullable(),
  color_code: z.string().nullable(),
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var scheduleEmployeeInfoSchema = z.object({
  id: int6,
  name: z.string(),
  pinfl: z.string(),
  image_id: z.string().nullable()
});
var detailedScheduleEventsResponseSchema = z.object({
  list: z.array(
    z.object({
      event: scheduleEventSchema,
      book: examBookSchema,
      employee: scheduleEmployeeInfoSchema.optional()
    })
  )
});
var groupedScheduleEventsResponseSchema = z.object({
  list: z.array(z.object({ month: int6, day: int6, events: z.array(scheduleEventSchema) }))
});
var scheduleEventsQuerySchema = paginationQuerySchema.extend({
  org_id: optionalInt2,
  employee_id: optionalInt2,
  book_id: optionalInt2,
  time_from: dateTimeString.optional(),
  time_to: dateTimeString.optional()
});
var updateScheduleEventSchema = z.object({
  event: z.string().min(1).optional(),
  time: dateTimeString.optional(),
  description: z.string().max(512).nullable().optional(),
  color_code: z.string().max(256).nullable().optional()
});
var updateScheduleEventResponseSchema = z.object({ event: scheduleEventSchema });
var deleteScheduleEventResponseSchema = z.object({ message: z.string() });
var EXAM_CODES = {
  EXAM_NOT_FOUND: "EXAM_NOT_FOUND",
  EXAM_ALREADY_EXIST: "EXAM_ALREADY_EXIST",
  EXAM_QUESTION_NOT_FOUND: "EXAM_QUESTION_NOT_FOUND",
  EXAM_QUESTION_ALREADY_EXIST: "EXAM_QUESTION_ALREADY_EXIST",
  INVALID_EMPLOYEE_BOOK_STATUS_TO_START_EXAM: "INVALID_EMPLOYEE_BOOK_STATUS_TO_START_EXAM",
  INVALID_EXAM_STATUS_TO_FINISH: "INVALID_EXAM_STATUS_TO_FINISH",
  PASSED_EXAM_MAX_COUNT_THRESHOLD_FOR_PERIOD: "PASSED_EXAM_MAX_COUNT_THRESHOLD_FOR_PERIOD",
  QUESTION_NOT_ENOUGH_TO_START_EXAM: "QUESTION_NOT_ENOUGH_TO_START_EXAM"
};

// src/exam/score.ts
var EXAM_SCORE_BANDS = [
  { min_percent: 90, tenths: 3 },
  { min_percent: 70, tenths: 2 },
  { min_percent: 40, tenths: 1 }
];
var MAX_EXAM_SCORE = 0.3;
function examPercent(correct, total) {
  if (total <= 0) return 0;
  return Math.trunc(correct * 100 / total);
}
function examScoreTenths(correct, total) {
  const percent = examPercent(correct, total);
  return EXAM_SCORE_BANDS.find((band) => percent >= band.min_percent)?.tenths ?? 0;
}
function examScore(correct, total) {
  return examScoreTenths(correct, total) / 10;
}
function sumExamScores(scores) {
  return scores.reduce((sum, score2) => sum + Math.round(score2 * 10), 0) / 10;
}
function formatExamScore(score2) {
  return score2.toFixed(1);
}
var int7 = z.number().int();
var logSchema = z.object({
  id: int7,
  type: z.string(),
  object_id: int7,
  object_type: z.string(),
  created_by: int7,
  created_at: dateTimeString
});
var createLogSchema = z.object({
  type: z.string().min(1),
  object_id: int7,
  object_type: z.string().min(1)
});
var listLogsQuerySchema = paginationQuerySchema.extend({
  type: z.string().optional(),
  object_id: z.coerce.number().int().optional(),
  object_type: z.string().optional(),
  created_by: z.coerce.number().int().optional()
});
var logListResponseSchema = pageResponseSchema(logSchema);
var LOG_CODES = { LOG_NOT_FOUND: "LOG_NOT_FOUND" };
var int8 = z.number().int();
var readStatsQuerySchema = z.object({
  org_id: z.coerce.number().int(),
  from_date: dateString.optional(),
  to_date: dateString.optional()
});
var sphereStats = z.object({ total_count: int8, read_count: int8 });
var readStatsResponseSchema = z.object({
  total: int8,
  ethical: sphereStats,
  professional: sphereStats,
  legal_disciplinary: sphereStats,
  spiritual_educational: sphereStats,
  physical: sphereStats
});
var leaderboardQuerySchema = paginationQuerySchema.extend({
  org_id: z.coerce.number().int().optional(),
  date_from: dateString.optional(),
  date_to: dateString.optional()
});
var leaderboardItemSchema = z.object({
  employee_id: int8,
  employee_name: z.string(),
  employee_pinfl: z.string(),
  employee_passport_image: z.string(),
  passed_books_count: int8,
  average_score: z.number(),
  /** Xodimning shu oraliqdagi BARCHA imtihonlari bali yig'indisi (yiqilganlari ham) */
  total_score: z.number()
});
var leaderboardResponseSchema = pageResponseSchema(leaderboardItemSchema);
var spiritualIndexQuerySchema = z.object({
  from_date: dateString.optional(),
  to_date: dateString.optional()
});
var sphereIndex = z.object({ index_percentage: z.number() });
var spiritualIndexResponseSchema = z.object({
  total_index_percentage: z.number(),
  ethical: sphereIndex,
  professional: sphereIndex,
  legal_disciplinary: sphereIndex,
  spiritual_educational: sphereIndex,
  physical: sphereIndex
});
var int9 = z.number().int();
var staffStatisticsQuerySchema = z.object({
  /**
   * Quyi tashkilotlar ham qo'shilsinmi (eski UI: `all`).
   * `z.stringbool()` EMAS: u transform, nestjs-zod esa Swagger uchun
   * `toJSONSchema()` ishlatadi va transform'ni chiza olmaydi (verify.sh
   * "OpenAPI yasaladi" darvozasi shuni ushlaydi).
   */
  all: z.enum(["true", "false"]).default("false")
});
var staffStatisticsResponseSchema = z.object({
  /**
   * Tashkilotdagi xodimlar soni — qo'lda kiritilgani bo'lsa u, aks holda HR
   * (`EmployeeCountService.resolve()`, pastdagi «Xodimlar soni» bloki).
   * `null` — son noma'lum. 0 emas, chunki "0 ta xodim" va
   * "son noma'lum" UI'da boshqacha ko'rsatilishi kerak.
   */
  employees_count: int9.nullable(),
  age: z.object({
    up_to_30: int9,
    from_30_to_40: int9,
    over_40: int9,
    retired: int9
  }),
  gender: z.object({
    male: int9,
    female: int9
  }),
  education: z.object({
    high: int9,
    medium_special: int9,
    medium: int9,
    none: int9
  })
});
var EMPLOYEE_COUNT_SOURCES = ["manual", "hr"];
var EMPLOYEE_COUNT_MAX = 1e6;
var employeeCountQuerySchema = z.object({
  org_id: z.coerce.number().int().positive()
});
var setEmployeeCountSchema = z.object({
  count: int9.min(1).max(EMPLOYEE_COUNT_MAX)
});
var employeeCountResponseSchema = z.object({
  org_id: int9,
  /** Hisob-kitoblarda ishlatiladigan son; `null` — qo'lda kiritilmagan va HR javob bermadi */
  count: int9.nullable(),
  /** `count` qayerdan olingani; `null` — `count` ham `null` */
  source: z.enum(EMPLOYEE_COUNT_SOURCES).nullable(),
  /** HR'dagi son (solishtirish uchun); `null` — HR javob bermadi */
  hr_count: int9.nullable(),
  /** Qo'lda kiritilgan qiymat; `null` — kiritilmagan */
  manual: z.object({
    count: int9,
    /** YYYY-MM-DD HH:mm:ss */
    updated_at: z.string(),
    /** Kim kiritgan (ism); noma'lum bo'lsa bo'sh satr */
    updated_by: z.string()
  }).nullable()
});
var int10 = z.number().int();
var AI_TEST_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  FAILED: "failed"
};
var AI_QUESTIONS_MAX = 1e3;
var AI_ONE_CORRECT_MESSAGE = "aynan bitta t\xF6\u011Fri javob b\xF6li\u015Fi kerak";
var aiQuestionOptionSchema = z.object({
  text: z.string().trim().min(1).max(255),
  is_correct: z.boolean()
});
var AI_SOURCE_TEXT_MAX = 1e4;
var pdfPageSchema = z.object({ pdf_page_number: int10.positive() });
var aiQuestionMetadataSchema = z.object({
  difficulty: z.string().trim().toLowerCase().pipe(questionDifficultySchema).nullish(),
  start: pdfPageSchema.nullish(),
  end: pdfPageSchema.nullish(),
  source_text: z.string().trim().max(AI_SOURCE_TEXT_MAX).nullish()
});
var aiQuestionSchema = z.object({
  /** AI'dagi tartib raqami (1..100) — saqlanmaydi; savollar kelgan tartibda yoziladi */
  question_number: int10.positive().optional(),
  question_text: z.string().trim().min(1).max(512),
  options: z.array(aiQuestionOptionSchema).min(2),
  metadata: aiQuestionMetadataSchema.nullish()
}).superRefine((q, ctx) => {
  if (q.options.filter((o) => o.is_correct).length !== 1) {
    ctx.addIssue({ code: "custom", path: ["options"], message: AI_ONE_CORRECT_MESSAGE });
  }
});
var aiQuestionsResultSchema = z.object({
  book_id: int10.positive(),
  /**
   * AI'dagi yuklash (`POST /books/upload` javobi) — natija aynan shu yuklashga
   * bog'lanadi (2026-10-15, `Desktop/Kitobxonlik-AI-document_id.md`).
   * Yuborilmasa — eski format: kitobning oxirgi yuklashi.
   */
  document_id: z.string().trim().min(1).max(64).optional(),
  status: z.enum([AI_TEST_STATUS.COMPLETED, AI_TEST_STATUS.FAILED]),
  questions: z.array(aiQuestionSchema).max(AI_QUESTIONS_MAX).default([]),
  error: z.string().max(2e3).optional()
});
var aiQuestionsResultResponseSchema = z.object({
  book_id: int10,
  saved_count: int10
});
var INTEGRATION_CODES = {
  /** Basic auth sarlavhasi yo'q, login yoki parol noto'g'ri */
  AUTH_FAILED: "INTEGRATION_AUTH_FAILED",
  /** Natija kitobning eski yuklashiniki — yangirog'i bor, savollar almashtirilmaydi (409) */
  STALE_DOCUMENT: "INTEGRATION_STALE_DOCUMENT"
};
var int11 = z.number().int();
var AI_JOB_STATUS = {
  QUEUED: "queued",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed"
};
var AI_DELIVERY_STATUS = {
  PENDING: "pending",
  DELIVERED: "delivered",
  FAILED: "failed"
};
var AI_PROCESS_ERROR = "Test jarayonida xatolik yuz berdi";
var AI_MAX_PDF_BYTES = 50 * 1024 * 1024;
var aiUploadStatusSchema = z.object({
  document_id: z.string().min(1),
  status: z.string().min(1),
  phase: z.string().nullish(),
  progress: z.number().nullish(),
  error: z.string().nullish(),
  delivery_status: z.string().nullish(),
  delivery_error: z.string().nullish()
});
var aiLibraryItemSchema = z.object({
  document_id: z.string().min(1),
  book_id: int11.nullish(),
  created_at: z.string().nullish()
});
var aiTestJobSchema = z.object({
  id: int11,
  book_id: int11,
  /** AI'dagi jarayon id'si (uuid) */
  document_id: z.string(),
  /** `AI_JOB_STATUS` (AI qaytarganicha) */
  status: z.string(),
  phase: z.string().nullable(),
  /** 0..100 */
  progress: int11.nullable(),
  /** AI generatsiya xatosi bo'lsa — `AI_PROCESS_ERROR` (asl matni faqat bazada) */
  error: z.string().nullable(),
  /** `AI_DELIVERY_STATUS` — AI savollarni bizga yubordimi */
  delivery_status: z.string().nullable(),
  /** Yetkazish xatosi bo'lsa — `AI_PROCESS_ERROR` (asl matni faqat bazada) */
  delivery_error: z.string().nullable(),
  /** Savollar bizga kelib saqlangan bo'lsa — nechtasi va qachon */
  saved_count: int11.nullable(),
  received_at: dateTimeString.nullable(),
  /** So'rov yuborgan foydalanuvchi (`users.id`) */
  requested_by: int11.nullable(),
  /** Kitob AI'ga AI tomonida yuklangan (biz yubormaganmiz) — natija kelganda yozilgan */
  from_ai: z.boolean(),
  created_at: dateTimeString,
  updated_at: dateTimeString
});
var bookAiTestsResponseSchema = z.object({
  job: aiTestJobSchema.nullable(),
  /**
   * Holatni AI'dan yangilab bo'lmadi — `AI_PROCESS_ERROR` (sababi server
   * log'ida); `job` bazadagi oxirgi holat
   */
  refresh_error: z.string().nullable()
});
var activeAiTestSchema = bookAiTestsResponseSchema.extend({
  job: aiTestJobSchema,
  book_title: z.string()
});
var activeAiTestsResponseSchema = z.object({
  items: z.array(activeAiTestSchema)
});
var aiTestsPullResponseSchema = z.object({
  /** Kitobga yozilgan savollar (eskilari almashtirildi) */
  saved_count: int11,
  /** Kitobning oxirgi so'rovi (natija unga bog'langan bo'lsa — «qabul qilindi») */
  job: aiTestJobSchema.nullable()
});
function isAiJobSettled(job) {
  if (job.received_at) return true;
  if (job.status === AI_JOB_STATUS.FAILED) return true;
  return job.status === AI_JOB_STATUS.COMPLETED && (job.delivery_status === AI_DELIVERY_STATUS.FAILED || job.delivery_status === AI_DELIVERY_STATUS.DELIVERED);
}
function isAiJobMisdelivered(job) {
  return !job.received_at && job.status === AI_JOB_STATUS.COMPLETED && job.delivery_status === AI_DELIVERY_STATUS.DELIVERED;
}
var AI_CODES = {
  /** AI serveriga ulanib bo'lmadi, javob kutilgan vaqtda kelmadi yoki 5xx */
  UNAVAILABLE: "AI_UNAVAILABLE",
  /** AI Kitobxonlik login-parolini (yoki tokenini) qabul qilmadi */
  AUTH_FAILED: "AI_AUTH_FAILED",
  /** AI so'rovni rad etdi (400/413/422 va h.k.) — sababi xabarda */
  REQUEST_FAILED: "AI_REQUEST_FAILED",
  /** AI bu `document_id` ni tanimaydi (holat so'rovi 404) */
  JOB_NOT_FOUND: "AI_JOB_NOT_FOUND",
  /** AI bazasida kitobning tayyor testi yo'q (qayta olish) */
  RESULT_NOT_FOUND: "AI_RESULT_NOT_FOUND",
  /** Shu kitob AI'da hozir tahlil qilinmoqda (AI 409) */
  BOOK_BUSY: "AI_BOOK_BUSY",
  /** Kitobning elektron varianti yo'q */
  EBOOK_MISSING: "AI_EBOOK_MISSING",
  /** Elektron kitob PDF emas */
  EBOOK_NOT_PDF: "AI_EBOOK_NOT_PDF",
  /** PDF AI chegarasidan katta (`AI_MAX_PDF_BYTES`) */
  EBOOK_TOO_LARGE: "AI_EBOOK_TOO_LARGE"
};
var PERM = {
  /** Kutubxonani ko'rish: kitoblar ro'yxati, kitob sahifasi, o'z ko'rsatkichlari */
  LIBRARY_READ: "library:read",
  /** Sevimliga qo'shish, baholash, imtihonni rejalashtirish */
  LIBRARY_INTERACT: "library:interact",
  /** Imtihon topshirish: boshlash/yakunlash, video chunk, qoidabuzarlik jurnali */
  EXAM_TAKE: "exam:take",
  /** Boshqaruvda kitoblar ro'yxati va umumiy hisob-kitoblar (read-stats, leaderboard) */
  BOOK_READ: "book:read",
  /** Kitob yaratish/tahrirlash/o'chirish */
  BOOK_WRITE: "book:write",
  /** Savollarni ko'rish */
  QUESTION_READ: "question:read",
  /** Savol yaratish/tahrirlash/o'chirish/Excel import */
  QUESTION_WRITE: "question:write",
  /** Lug'atlar (ruknlar, fanlar) ro'yxati */
  DICTIONARY_READ: "dictionary:read",
  /** Lug'atlarni tahrirlash */
  DICTIONARY_WRITE: "dictionary:write",
  /** Fayl yuklash (muqova, e-kitob, audio, savol rasmi) */
  FILE_UPLOAD: "file:upload",
  /** Boshqaruvda imtihonlar ro'yxati, rejalashtirilganlar, kalendar */
  EXAM_READ: "exam:read",
  /** Imtihon holatini o'zgartirish (failed -> ...) */
  EXAM_MODERATE: "exam:moderate",
  /** Qoidabuzarlik jurnalini ko'rish */
  LOG_READ: "log:read",
  /**
   * Tashkilotlar (tizimga kirgan xodimlarning tashkilotlari), kitobga
   * biriktirilganlari va kutubxona a'zolari. Faqat SUPER_ADMIN va DEVELOPER.
   */
  ORGANIZATION_READ: "organization:read",
  /** Kitobni tashkilotlarga biriktirish, a'zo tashkilot qo'shish/olib tashlash. Faqat SUPER_ADMIN va DEVELOPER */
  ORGANIZATION_WRITE: "organization:write",
  /** Umumiy statistika (yosh/jins/ma'lumot kesimlari) */
  STATISTICS_READ: "statistics:read",
  /** Tashkilot xodimlari sonini qo'lda kiritish (HR'dagi sondan ustun) */
  STATISTICS_WRITE: "statistics:write",
  /** Rollar va ularning ruxsatlarini ko'rish */
  ROLE_READ: "role:read",
  /** Rolga ruxsat biriktirish/olib tashlash, rol yaratish */
  ROLE_WRITE: "role:write",
  /** Foydalanuvchilar ro'yxati */
  USER_READ: "user:read",
  /** Foydalanuvchiga rol biriktirish, faolligini o'zgartirish */
  USER_WRITE: "user:write",
  /**
   * Foydalanuvchini o'chirish (soft delete — bazada `deleted_at` bilan qoladi)
   * — imtihonlari, videolari, kitob holatlari va boshqa izlari bilan. Faqat
   * DEVELOPER (pastga qarang).
   */
  USER_PURGE: "user:purge",
  /**
   * Imtihonni o'chirish (soft delete) — savollari va javoblari,
   * qoidabuzarlik jurnali va videosi bilan; xodimning kitob holati qolgan
   * imtihonlardan qayta hisoblanadi. Faqat DEVELOPER.
   */
  EXAM_DELETE: "exam:delete"
};
var PERMISSION_GROUPS = {
  LIBRARY: "library",
  EXAM: "exam",
  CONTENT: "content",
  ANALYTICS: "analytics",
  ACCESS: "access",
  DEVELOPER: "developer"
};
var PERMISSION_CATALOG = [
  { code: PERM.LIBRARY_READ, group: PERMISSION_GROUPS.LIBRARY, name: "Kutubxonani k\xF6ri\u015F" },
  { code: PERM.LIBRARY_INTERACT, group: PERMISSION_GROUPS.LIBRARY, name: "Sevimli, baho, reja" },
  { code: PERM.EXAM_TAKE, group: PERMISSION_GROUPS.EXAM, name: "Imtihon top\u015Firi\u015F" },
  { code: PERM.BOOK_READ, group: PERMISSION_GROUPS.CONTENT, name: "Kitoblarni k\xF6ri\u015F" },
  { code: PERM.BOOK_WRITE, group: PERMISSION_GROUPS.CONTENT, name: "Kitoblarni tahrirla\u015F" },
  { code: PERM.QUESTION_READ, group: PERMISSION_GROUPS.CONTENT, name: "Savollarni k\xF6ri\u015F" },
  { code: PERM.QUESTION_WRITE, group: PERMISSION_GROUPS.CONTENT, name: "Savollarni tahrirla\u015F" },
  { code: PERM.DICTIONARY_READ, group: PERMISSION_GROUPS.CONTENT, name: "Lu\u011Fatlarni k\xF6ri\u015F" },
  { code: PERM.DICTIONARY_WRITE, group: PERMISSION_GROUPS.CONTENT, name: "Lu\u011Fatlarni tahrirla\u015F" },
  { code: PERM.FILE_UPLOAD, group: PERMISSION_GROUPS.CONTENT, name: "Fayl yukla\u015F" },
  { code: PERM.EXAM_READ, group: PERMISSION_GROUPS.EXAM, name: "Imtihonlarni k\xF6ri\u015F" },
  {
    code: PERM.EXAM_MODERATE,
    group: PERMISSION_GROUPS.EXAM,
    name: "Imtihon holatini \xF6zgartiri\u015F"
  },
  { code: PERM.LOG_READ, group: PERMISSION_GROUPS.EXAM, name: "Qoidabuzarlik jurnali" },
  {
    code: PERM.ORGANIZATION_READ,
    group: PERMISSION_GROUPS.ANALYTICS,
    name: "Ta\u015Fkilotlarni k\xF6ri\u015F"
  },
  {
    code: PERM.ORGANIZATION_WRITE,
    group: PERMISSION_GROUPS.ANALYTICS,
    name: "Ta\u015Fkilotlarga kitob biriktiri\u015F"
  },
  { code: PERM.STATISTICS_READ, group: PERMISSION_GROUPS.ANALYTICS, name: "Umumiy statistika" },
  {
    code: PERM.STATISTICS_WRITE,
    group: PERMISSION_GROUPS.ANALYTICS,
    name: "Xodimlar sonini kiriti\u015F"
  },
  { code: PERM.ROLE_READ, group: PERMISSION_GROUPS.ACCESS, name: "Rollarni k\xF6ri\u015F" },
  { code: PERM.ROLE_WRITE, group: PERMISSION_GROUPS.ACCESS, name: "Rollarni tahrirla\u015F" },
  { code: PERM.USER_READ, group: PERMISSION_GROUPS.ACCESS, name: "Foydalanuv\xE7ilarni k\xF6ri\u015F" },
  { code: PERM.USER_WRITE, group: PERMISSION_GROUPS.ACCESS, name: "Foydalanuv\xE7i rollari" },
  {
    code: PERM.USER_PURGE,
    group: PERMISSION_GROUPS.DEVELOPER,
    name: "Foydalanuv\xE7ini \xF6\xE7iri\u015F"
  },
  {
    code: PERM.EXAM_DELETE,
    group: PERMISSION_GROUPS.DEVELOPER,
    name: "Imtihonni \xF6\xE7iri\u015F"
  }
];
var ALL_PERMISSIONS = PERMISSION_CATALOG.map((p) => p.code);
var DEVELOPER_ONLY_PERMISSIONS = [
  PERM.USER_PURGE,
  PERM.EXAM_DELETE
];
function isDeveloperOnlyPermission(code) {
  return DEVELOPER_ONLY_PERMISSIONS.includes(code);
}
var SUPER_ADMIN_PERMISSIONS = [
  PERM.ORGANIZATION_READ,
  PERM.ORGANIZATION_WRITE
];
function isSuperAdminPermission(code) {
  return SUPER_ADMIN_PERMISSIONS.includes(code);
}
var ROLE = {
  STAFF: "STAFF",
  MODERATOR: "MODERATOR",
  ADMIN: "ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  DEVELOPER: "DEVELOPER"
};
var SYSTEM_ROLES = [
  {
    code: ROLE.STAFF,
    name: "Xodim",
    description: "Kutubxonadan foydalanadi va imtihon top\u015Firadi"
  },
  {
    code: ROLE.MODERATOR,
    name: "Moderator",
    description: "Kuzatadi: kitoblar, imtihonlar, jurnal va statistika \u2014 tahrirlamaydi"
  },
  {
    code: ROLE.ADMIN,
    name: "Administrator",
    description: "Bar\xE7a ruxsatlar, \u015Fu jumladan rollarni bo\u015Fqari\u015F"
  },
  {
    code: ROLE.SUPER_ADMIN,
    name: "Super administrator",
    description: "Administrator ruxsatlari va ta\u015Fkilotlar: k\xF6ri\u015F, kitob biriktiri\u015F"
  },
  {
    code: ROLE.DEVELOPER,
    name: "Dastur\xE7i",
    description: "Bar\xE7a ruxsatlar va nozik amallar (foydalanuv\xE7ini va imtihonni \xF6\xE7iri\u015F)"
  }
];
var DEVELOPER_MANAGED_ROLES = [ROLE.SUPER_ADMIN, ROLE.DEVELOPER];
function isDeveloperManagedRole(code) {
  return DEVELOPER_MANAGED_ROLES.includes(code);
}
function isPermissionAllowedForRole(permission, roleCode) {
  if (isDeveloperOnlyPermission(permission)) return roleCode === ROLE.DEVELOPER;
  if (isSuperAdminPermission(permission)) {
    return roleCode === ROLE.SUPER_ADMIN || roleCode === ROLE.DEVELOPER;
  }
  return true;
}
var DEFAULT_ROLE = ROLE.STAFF;
var DEFAULT_ROLE_PERMISSIONS = {
  [ROLE.STAFF]: [PERM.LIBRARY_READ, PERM.LIBRARY_INTERACT, PERM.EXAM_TAKE],
  [ROLE.MODERATOR]: [
    PERM.BOOK_READ,
    PERM.QUESTION_READ,
    PERM.EXAM_READ,
    PERM.LOG_READ,
    PERM.STATISTICS_READ
  ],
  [ROLE.ADMIN]: ALL_PERMISSIONS.filter((code) => isPermissionAllowedForRole(code, ROLE.ADMIN)),
  [ROLE.SUPER_ADMIN]: ALL_PERMISSIONS.filter(
    (code) => isPermissionAllowedForRole(code, ROLE.SUPER_ADMIN)
  ),
  [ROLE.DEVELOPER]: ALL_PERMISSIONS
};
var permissionSchema = z.object({
  code: z.string(),
  group: z.string(),
  name: z.string()
});
var roleSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string(),
  is_system: z.boolean(),
  /** Shu rolga biriktirilgan ruxsat kodlari */
  permissions: z.array(z.string()),
  /** Shu rolga ega foydalanuvchilar soni */
  users_count: z.number().int()
});
var rolesResponseSchema = z.object({
  data: z.array(roleSchema),
  /** Katalog — UI matritsa ustunlarini shundan yasaydi */
  permissions: z.array(permissionSchema)
});
var setRolePermissionsSchema = z.object({
  permissions: z.array(z.string()).max(ALL_PERMISSIONS.length)
});
var createRoleSchema = z.object({
  code: z.string().trim().min(2).max(50).regex(/^[A-Z][A-Z0-9_]*$/, "Faqat BO\u015E HARF, raqam va pastki \xE7iziq"),
  name: z.string().trim().min(2).max(255),
  description: z.string().trim().max(500).default(""),
  permissions: z.array(z.string()).default([])
});
var updateRoleSchema = z.object({
  name: z.string().trim().min(2).max(255),
  description: z.string().trim().max(500).default("")
});
var oneIdRequestSchema = z.object({
  /** SSO callback'dagi `?code=` */
  code: z.string().trim().min(1).max(4096),
  /** Frontend saqlagan PKCE `code_verifier` */
  code_verifier: z.string().trim().min(43).max(128),
  /**
   * Login'da ishlatilgan AYNAN o'sha `redirectUri` — aks holda SSO token bermaydi.
   * Backend uni ruxsat etilgan ro'yxat bilan tekshiradi (ochiq redirect bo'lmasin).
   */
  redirect_uri: z.string().trim().url().max(2048)
});
var hrEntryRequestSchema = z.object({
  /** URL'dagi `?p=` — shifrlangan PINFL (AES-256-GCM, base64url) */
  p: z.string().trim().min(1).max(2048)
});
var AUTH_METHODS = ["one_id", "hr_entry", "dev", "pinfl"];
var authMethodSchema = z.enum(AUTH_METHODS);
var authUserSchema = z.object({
  /** Bizning `users.id` */
  user_id: z.number().int(),
  /** HR tizimidagi xodim id'si — imtihon/kitob jadvallaridagi `employee_id` */
  employee_id: z.number().int(),
  pinfl: z.string(),
  org_id: z.number().int(),
  /**
   * Tashkilot nomi — HR `state` beradi (`organizations` jadvali). `null` —
   * HR nom bermagan: UI o'rniga `org_id` ni ko'rsatadi.
   */
  org_name: z.string().nullable(),
  name: z.string(),
  passport_image: z.string().nullable(),
  /**
   * OneID introspect beradigan maydonlar (`birth_date`, `phone_number`).
   * HR `state` servisi bermaydi, shuning uchun HR'dan o'tishda `null` bo'lishi
   * mumkin — format ham SSO qanday bersa shunday saqlanadi (matn).
   */
  birth_date: z.string().nullable(),
  phone: z.string().nullable(),
  /** Rol KODLARI (STAFF, MODERATOR, ADMIN, ...) */
  roles: z.array(z.string()),
  /** Rollardan yig'ilgan ruxsat kodlari — UI shuni tekshiradi, rol nomini emas */
  permissions: z.array(z.string())
});
var authSessionResponseSchema = z.object({
  user: authUserSchema,
  /** Access token qachon tugaydi (ISO) — frontend oldindan yangilashi uchun */
  expires_at: z.string(),
  method: authMethodSchema
});
var pinflSchema = z.string().trim().regex(/^\d{14}$/, "PINFL 14 raqam");
var devLoginRequestSchema = z.object({
  pinfl: pinflSchema
});
var pinflLoginRequestSchema = z.object({
  pinfl: pinflSchema
});
var devHrLinkResponseSchema = z.object({
  p: z.string()
});

// src/auth/me.schema.ts
var meResponseSchema = authUserSchema;
var appUserSchema = z.object({
  id: z.number().int(),
  pinfl: z.string(),
  employee_id: z.number().int(),
  org_id: z.number().int(),
  /** Tashkilot nomi (HR); `null` — nom yo'q, UI `org_id` ni ko'rsatadi */
  org_name: z.string().nullable(),
  name: z.string(),
  is_active: z.boolean(),
  roles: z.array(z.string()),
  /** Oxirgi muvaffaqiyatli kirish (YYYY-MM-DD HH:mm:ss) yoki null */
  last_login_at: z.string().nullable(),
  created_at: z.string()
});
var listUsersQuerySchema = paginationQuerySchema.extend({
  /** PINFL yoki ism bo'yicha qidiruv */
  search: z.string().trim().max(255).optional(),
  /** Rol kodi bo'yicha filtr */
  role: z.string().trim().max(50).optional(),
  org_id: z.coerce.number().int().optional()
});
var setUserRolesSchema = z.object({
  roles: z.array(z.string().trim().max(50)).max(50)
});
var lookupUserRequestSchema = z.object({
  pinfl: pinflSchema
});
var userLookupResponseSchema = z.object({
  pinfl: z.string(),
  employee_id: z.number().int(),
  org_id: z.number().int(),
  /** Tashkilot nomi; `null` — nom yo'q, UI `org_id` ni ko'rsatadi */
  org_name: z.string().nullable(),
  name: z.string(),
  user_id: z.number().int().nullable()
});
var createUserRequestSchema = z.object({
  pinfl: pinflSchema,
  roles: z.array(z.string().trim().max(50)).min(1, "Kamida bitta rol").max(50)
});
var setUserStatusSchema = z.object({
  is_active: z.boolean()
});
var purgeUserResponseSchema = z.object({
  user_id: z.number().int(),
  pinfl: z.string(),
  deleted: z.object({
    exams: z.number().int(),
    recordings: z.number().int(),
    employee_books: z.number().int(),
    favorites: z.number().int(),
    downloads: z.number().int(),
    reviews: z.number().int(),
    schedules: z.number().int(),
    logs: z.number().int(),
    sessions: z.number().int()
  })
});

export { AI_CODES, AI_DELIVERY_STATUS, AI_JOB_STATUS, AI_MAX_PDF_BYTES, AI_ONE_CORRECT_MESSAGE, AI_PROCESS_ERROR, AI_QUESTIONS_MAX, AI_SOURCE_TEXT_MAX, AI_TEST_STATUS, ALL_PERMISSIONS, AUTH_METHODS, BOOK_CODES, BUCKETS, CHEAT_LOG_TYPES, CLIENT_FINISH_REASONS, DATE_FORMAT, DATE_TIME_FORMAT, DEFAULT_DIFFICULTY_MIX, DEFAULT_ROLE, DEFAULT_ROLE_PERMISSIONS, DEVELOPER_MANAGED_ROLES, DEVELOPER_ONLY_PERMISSIONS, EBOOK_MAX_BYTES, EBOOK_MAX_MB, EMPLOYEE_BOOK_STATUSES, EMPLOYEE_COUNT_MAX, EMPLOYEE_COUNT_SOURCES, ERROR_CODES, EXAM_CODES, EXAM_FINISH_REASONS, EXAM_SCORE_BANDS, EXAM_STATUSES, EXAM_VIOLATION_LIMITS, FILE_CODES, FILE_TYPES, HR_ERROR_CODES, INTEGRATION_CODES, LIBRARY_CODES, LOG_CODES, MAX_EXAM_SCORE, PAGINATION_DEFAULT_SIZE, PAGINATION_MAX_SIZE, PERM, PERMISSION_CATALOG, PERMISSION_GROUPS, QUESTION_CODES, QUESTION_DIFFICULTIES, ROLE, SPIRITUAL_SPHERES, SUPER_ADMIN_PERMISSIONS, SYSTEM_ROLES, UPLOAD_FILE_TYPES, activeAiTestSchema, activeAiTestsResponseSchema, adminBookListItemSchema, adminBookListQuerySchema, adminBookListResponseSchema, adminExamListItemSchema, adminExamListQuerySchema, adminExamListResponseSchema, aiLibraryItemSchema, aiQuestionMetadataSchema, aiQuestionOptionSchema, aiQuestionSchema, aiQuestionsResultResponseSchema, aiQuestionsResultSchema, aiTestJobSchema, aiTestsPullResponseSchema, aiUploadStatusSchema, answerOptionSchema, answerOptionsSchema, apiErrorSchema, appUserSchema, authMethodSchema, authSessionResponseSchema, authUserSchema, bookAiTestsResponseSchema, bookDifficultyMixResponseSchema, bookOrganizationsResponseSchema, bookSchema, booksCountResponseSchema, buildPageResponse, changeExamStatusResponseSchema, changeExamStatusSchema, createBookSchema, createGenreSchema, createLogSchema, createQuestionSchema, createRoleSchema, createSubjectSchema, createUserRequestSchema, dateString, dateTimeString, deleteExamResponseSchema, deleteScheduleEventResponseSchema, detailedScheduleEventsResponseSchema, devHrLinkResponseSchema, devLoginRequestSchema, difficultyMixExceedsMessage, difficultyMixSchema, difficultyMixSumMessage, employeeBookItemSchema, employeeBookListQuerySchema, employeeBookListResponseSchema, employeeBookStatusSchema, employeeCountQuerySchema, employeeCountResponseSchema, employeeExamListItemSchema, employeeExamListQuerySchema, employeeExamListResponseSchema, examBookSchema, examEmployeeSchema, examFinishReasonSchema, examPercent, examQuestionSchema, examResponseSchema, examResultResponseSchema, examScore, examScoreTenths, examStatusSchema, examViolationLimitsSchema, fileUploadResponseSchema, finishExamSchema, formatExamScore, genreListResponseSchema, genreSchema, globalDifficultyMixResponseSchema, groupedScheduleEventsResponseSchema, hrEntryRequestSchema, hrErrorSchema, idParam, importQuestionsResponseSchema, importQuestionsSchema, isAiJobMisdelivered, isAiJobSettled, isDeveloperManagedRole, isDeveloperOnlyPermission, isExamFinishReason, isPermissionAllowedForRole, isSuperAdminPermission, latinFold, latinSearchVariants, leaderboardItemSchema, leaderboardQuerySchema, leaderboardResponseSchema, listLogsQuerySchema, listMemberOrgsQuerySchema, listQuestionsQuerySchema, listUsersQuerySchema, logListResponseSchema, logSchema, lookupUserRequestSchema, meResponseSchema, memberOrgListResponseSchema, memberOrgPairQuerySchema, normalizePagination, oneIdRequestSchema, orgIdQuerySchema, organizationBookSchema, organizationDetailSchema, organizationItemSchema, organizationsResponseSchema, pageRangeSchema, pageResponseSchema, paginationQuerySchema, permissionSchema, pinflLoginRequestSchema, pinflSchema, purgeUserResponseSchema, questionDifficultySchema, questionListResponseSchema, questionSchema, rateBookSchema, readStatsQuerySchema, readStatsResponseSchema, reviewPageRanges, rfc3339String, roleSchema, rolesResponseSchema, scheduleEmployeeInfoSchema, scheduleEventSchema, scheduleEventsQuerySchema, scheduleExamSchema, scheduledListItemSchema, scheduledListQuerySchema, scheduledListResponseSchema, setBookOrganizationsSchema, setEmployeeCountSchema, setRolePermissionsSchema, setUserRolesSchema, setUserStatusSchema, spiritualIndexQuerySchema, spiritualIndexResponseSchema, spiritualSphereSchema, splitByDifficultyMix, staffStatisticsQuerySchema, staffStatisticsResponseSchema, startExamQuerySchema, subjectListResponseSchema, subjectSchema, sumExamScores, toLimitOffset, toNewLatin, toNewLatinText, toOldLatin, toggleFavoriteResponseSchema, updateBookSchema, updateGenreSchema, updateQuestionSchema, updateRoleSchema, updateScheduleEventResponseSchema, updateScheduleEventSchema, updateSubjectSchema, uploadFileQuerySchema, uploadFileTypeSchema, userLookupResponseSchema, uuidString };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map